/**
 * ProductVariantField — a capture-form field of type `product_variant`.
 *
 * Renders a button that opens the shared `ProductSearchModal` (the same variant
 * picker used by samples) and shows the current selection(s) as removable chips.
 *
 * The stored value follows the entity-reference contract:
 *   - single field (`allowMultiple` false): one { id, label } (see EntityRefValue)
 *   - multiple field (`allowMultiple` true): an { id, label }[]
 * The label is captured here so timelines/emails render without a lookup, but
 * the server re-derives the authoritative label on submit — the client label is
 * never trusted (see submit_capture._resolve_single_variant).
 *
 * This is the reference implementation for entity-reference field types; a
 * future type (e.g. a contact picker) follows the same value contract.
 */
import { useState } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { PiPlus, PiX } from 'react-icons/pi'
import FbButton from 'components/FbUI/FbButton'
import { ErrorLabel } from 'components/FormUtils/ErrorLabel'
import { ProductSearchModal } from 'components/UI/ProductSearchModal/ProductSearchModal'
import type {
  ProductVariantSelection,
  SelectedVariant,
} from 'components/UI/ProductSearchModal/types'
import type { EntityRefValue } from 'models/captureForms'

interface ProductVariantFieldProps<T extends FieldValues> {
  control: Control<T, any>
  name: Path<T>
  label: string
  /** When true, allow selecting more than one variant (stored as an array). */
  allowMultiple?: boolean
}

// Mirrors the server's authoritative label format
// (submit_capture._resolve_single_variant): "<product> — <variant>".
function variantLabel(v: SelectedVariant): string {
  return v.product_name ? `${v.product_name} — ${v.name}` : v.name
}

function toEntityRef(v: SelectedVariant): EntityRefValue {
  return { id: v.id, label: variantLabel(v) }
}

export function ProductVariantField<T extends FieldValues>({
  control,
  name,
  label,
  allowMultiple = false,
}: ProductVariantFieldProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController({ control, name })
  const [open, setOpen] = useState(false)

  // Normalize the stored value to an array for uniform rendering; write it back
  // in the field's declared shape (single object vs array) so the value
  // contract the server expects is preserved.
  const raw = field.value as
    | EntityRefValue
    | EntityRefValue[]
    | null
    | undefined
  const selected: EntityRefValue[] = Array.isArray(raw) ? raw : raw ? [raw] : []

  function commit(next: EntityRefValue[]) {
    field.onChange(allowMultiple ? next : (next[0] ?? null))
  }

  function handleSelect(selection: ProductVariantSelection) {
    const refs = (selection.variants ?? []).map(toEntityRef)
    commit(allowMultiple ? refs : refs.slice(0, 1))
    setOpen(false)
  }

  function removeAt(id: number) {
    commit(selected.filter((v) => v.id !== id))
  }

  const emptyText = allowMultiple ? 'Select products' : 'Select product'
  const buttonText =
    selected.length > 0 && !allowMultiple ? 'Change product' : emptyText

  return (
    <div className="mb-2">
      <label className="mb-1 block text-sm font-medium text-fg" htmlFor={name}>
        {label}
      </label>

      {selected.length > 0 && (
        <ul
          className="mb-1 flex flex-wrap gap-1"
          aria-label={`${label} selections`}
        >
          {selected.map((v) => (
            <li
              key={v.id}
              className="flex items-center gap-1 rounded border border-line bg-surface-sunken px-2 py-0.5 text-sm text-fg"
            >
              <span className="truncate">{v.label}</span>
              <button
                type="button"
                onClick={() => removeAt(v.id)}
                aria-label={`Remove ${v.label}`}
                className="text-fg-tertiary hover:text-fg-secondary"
              >
                <PiX size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <FbButton
        id={name}
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <PiPlus size={14} />
        {buttonText}
      </FbButton>

      {/* ErrorLabel always renders its warning icon, so only mount it when
          there's an actual message — otherwise a stray alert icon shows. */}
      {error?.message && <ErrorLabel message={error.message} />}

      {open && (
        <ProductSearchModal
          show={open}
          onHide={() => setOpen(false)}
          selectionMode="variants-only"
          multiSelect={allowMultiple}
          maxSelections={allowMultiple ? 100 : 1}
          title={allowMultiple ? 'Select Variants' : 'Select Variant'}
          selectButtonText={allowMultiple ? 'Add Variants' : 'Select'}
          showProductFilter
          onSelect={handleSelect}
          initialSelection={
            selected.length > 0
              ? {
                  mode: 'variants-only',
                  // Pre-selection is matched by id; the other fields are unread
                  // here, so a minimal shape (label as name) is sufficient.
                  variants: selected.map((v) => ({
                    id: v.id,
                    name: v.label,
                    sku: '',
                    gtin: null,
                    avg_price: null,
                    format_pack_size: null,
                    product_id: null,
                    product_name: null,
                    distributor_codes: [],
                  })),
                }
              : undefined
          }
        />
      )}
    </div>
  )
}
